import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Grid3x3, ArrowRight, Sparkles, Pin, Zap } from "lucide-react";
import SaturateRowModal from "@/components/SaturateRowModal";

// MUTUAL-FIT MATRIX — heatmap of all firms × all firms with saved-synergy fit scores.
// Pure frontend composition over /firms + /synergies endpoints. No new backend route.
//
// Rows = FIRM_A (seller). Columns = FIRM_B (target). Cell color/intensity tracks fit_score.
// Diagonal is muted. Empty cells show a faint "+" link to /app/synergy?a=X&b=Y for scoring.
// Pin button on existing scored synergy rows is preserved.

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return `$${Number(n).toFixed(0)}`;
};

// Fit → background tone. Emerald >=90, blue 80-89, amber 70-79, zinc dim <70.
function fitTone(fit) {
    if (fit >= 90) return { bg: "rgba(16,185,129,0.78)", border: "#10B981", text: "#022c22" };
    if (fit >= 80) return { bg: "rgba(59,130,246,0.72)", border: "#3B82F6", text: "#0a1731" };
    if (fit >= 70) return { bg: "rgba(245,158,11,0.78)", border: "#F59E0B", text: "#1a0f00" };
    if (fit >= 50) return { bg: "rgba(113,113,122,0.55)", border: "#52525B", text: "#fafafa" };
    return { bg: "rgba(63,63,70,0.35)", border: "#3F3F46", text: "#a1a1aa" };
}

// Reused empty Set for matrix rows that have zero saved synergies — keeps Set identity stable
// so SaturateRowModal's useEffect doesn't refire after onSaved reload.
const EMPTY_SET = new Set();

export default function MutualFitMatrix() {
    const [firms, setFirms] = useState(null);
    const [synergies, setSynergies] = useState(null);
    const [error, setError] = useState("");
    const [showOnlyHighFit, setShowOnlyHighFit] = useState(false);
    const [hoverCell, setHoverCell] = useState(null);
    const [saturateFirm, setSaturateFirm] = useState(null);

    const reload = async () => {
        try {
            const [{ data: f }, { data: s }] = await Promise.all([
                api.get("/firms"),
                api.get("/synergies"),
            ]);
            setFirms(f || []);
            setSynergies(s || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load matrix.");
        }
    };

    useEffect(() => { reload(); }, []);

    // Build a quick lookup: best synergy per (firm_a_id, firm_b_id).
    // If multiple products are scored for the same pair, the highest-fit one wins for the cell.
    const cellMap = useMemo(() => {
        const map = new Map();
        (synergies || []).forEach((s) => {
            const key = `${s.firm_a_id}::${s.firm_b_id}`;
            const prev = map.get(key);
            if (!prev || (s.fit_score || 0) > (prev.fit_score || 0)) {
                map.set(key, s);
            }
        });
        return map;
    }, [synergies]);

    // For each source firm, the stable Set of already-scored target firm IDs.
    // Memoized so the modal effect doesn't re-fire after onSaved → reload() unless
    // the set membership actually changes for the currently-open firm.
    const scoredIdsByFirm = useMemo(() => {
        const out = new Map();
        (synergies || []).forEach((s) => {
            if (!out.has(s.firm_a_id)) out.set(s.firm_a_id, new Set());
            out.get(s.firm_a_id).add(s.firm_b_id);
        });
        return out;
    }, [synergies]);

    // Color firms with low/no synergies last so the "hot rows" cluster at the top.
    const sortedFirms = useMemo(() => {
        if (!firms) return [];
        const rowHeat = new Map();
        firms.forEach((f) => {
            let totalFit = 0;
            firms.forEach((g) => {
                if (g.id === f.id) return;
                const s = cellMap.get(`${f.id}::${g.id}`);
                if (s) totalFit += s.fit_score || 0;
            });
            rowHeat.set(f.id, totalFit);
        });
        return [...firms].sort((a, b) => (rowHeat.get(b.id) || 0) - (rowHeat.get(a.id) || 0));
    }, [firms, cellMap]);

    if (error) {
        return (
            <div className="p-8" data-testid="matrix-page-error">
                <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono">{error}</div>
            </div>
        );
    }
    if (!firms || !synergies) return <div className="p-12"><Loader label="Loading matrix" /></div>;

    if (firms.length === 0) {
        return (
            <div className="p-8" data-testid="matrix-page-empty">
                <header className="mb-8 border-b border-zinc-800 pb-6">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Matrix</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Mutual-fit matrix</h1>
                </header>
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Grid3x3 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="text-sm text-zinc-400">Add at least 2 firms to your library to render the matrix.</div>
                    <Link to="/app/research" className="inline-flex items-center gap-1.5 mt-4 font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400">
                        Run your first research <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        );
    }

    // Stats
    const totalCells = sortedFirms.length * (sortedFirms.length - 1);
    const scoredCells = cellMap.size;
    const highFitCells = [...cellMap.values()].filter((s) => (s.fit_score || 0) >= 80).length;
    const coverage = totalCells > 0 ? Math.round((scoredCells / totalCells) * 100) : 0;

    return (
        <div className="p-8" data-testid="matrix-page">
            <header className="mb-6 flex items-end justify-between border-b border-zinc-800 pb-6 gap-4 flex-wrap">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Matrix</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Mutual-fit matrix</h1>
                    <p className="text-sm text-zinc-500 mt-2">
                        Every firm pair, scored or scorable. Click a scored cell to reopen; click an empty cell to score it now.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowOnlyHighFit((v) => !v)}
                        data-testid="matrix-filter-high"
                        className={`text-xs font-mono uppercase tracking-wider px-3 py-2 border transition-colors ${
                            showOnlyHighFit ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                        }`}
                    >
                        ≥80 fit only
                    </button>
                </div>
            </header>

            {/* Stat strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-800/60 border border-zinc-800 mb-6" data-testid="matrix-stats">
                <Stat label="Firms" value={sortedFirms.length} />
                <Stat label="Pairs" value={totalCells} />
                <Stat label="Scored" value={`${scoredCells} · ${coverage}%`} accent />
                <Stat label="High fit (≥80)" value={highFitCells} accent />
            </div>

            {/* Matrix scroll wrapper */}
            <div className="border border-zinc-800 bg-zinc-950 overflow-x-auto" data-testid="matrix-grid-wrap">
                <table className="border-collapse" style={{ minWidth: 80 + sortedFirms.length * 56 }}>
                    <thead>
                        <tr>
                            <th className="sticky left-0 bg-zinc-950 z-10 px-3 py-3 border-b border-zinc-800 text-left">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">SOURCE \ TARGET</div>
                            </th>
                            {sortedFirms.map((f) => (
                                <th key={f.id} className="px-1 py-3 border-b border-zinc-800 align-bottom">
                                    <div className="flex flex-col items-center gap-1.5">
                                        {f.color ? <span className="w-2 h-2 rounded-full" style={{ background: f.color }} /> : <span className="w-2 h-2" />}
                                        <Link
                                            to={`/app/firms/${f.id}`}
                                            data-testid={`matrix-col-${f.id}`}
                                            className="font-mono text-[10px] uppercase tracking-wider text-zinc-300 hover:text-amber-400 transition-colors whitespace-nowrap"
                                            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", maxHeight: 90 }}
                                            title={f.name}
                                        >
                                            {f.name.length > 14 ? f.name.slice(0, 13) + "…" : f.name}
                                        </Link>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedFirms.map((rowFirm) => (
                            <tr key={rowFirm.id} className="border-b border-zinc-800/50">
                                <th className="sticky left-0 bg-zinc-950 z-10 px-3 py-1.5 text-left border-r border-zinc-800/50" style={{ minWidth: 180 }}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Link
                                            to={`/app/firms/${rowFirm.id}`}
                                            data-testid={`matrix-row-${rowFirm.id}`}
                                            className="flex items-center gap-2 min-w-0 group flex-1"
                                        >
                                            {rowFirm.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: rowFirm.color }} />}
                                            <span className="font-mono text-[11px] text-zinc-200 group-hover:text-amber-400 truncate">{rowFirm.name}</span>
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={() => setSaturateFirm(rowFirm)}
                                            data-testid={`matrix-saturate-${rowFirm.id}`}
                                            title={`Find partners for ${rowFirm.name}`}
                                            className="shrink-0 inline-flex items-center gap-1 border border-zinc-800 hover:border-amber-500 hover:bg-amber-500/10 hover:text-amber-400 text-zinc-500 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 transition-colors"
                                        >
                                            <Zap className="w-2.5 h-2.5" /> Saturate
                                        </button>
                                    </div>
                                </th>
                                {sortedFirms.map((colFirm) => {
                                    if (rowFirm.id === colFirm.id) {
                                        return (
                                            <td key={colFirm.id} className="p-0.5">
                                                <div className="w-[52px] h-[36px] bg-black border border-zinc-800/60 flex items-center justify-center">
                                                    <span className="font-mono text-[10px] text-zinc-700">·</span>
                                                </div>
                                            </td>
                                        );
                                    }
                                    const s = cellMap.get(`${rowFirm.id}::${colFirm.id}`);
                                    const fit = s?.fit_score || 0;
                                    const hide = showOnlyHighFit && fit < 80;
                                    if (!s) {
                                        // Empty cell — clickable shortcut to score
                                        return (
                                            <td key={colFirm.id} className="p-0.5">
                                                <Link
                                                    to={`/app/synergy?a=${rowFirm.id}&b=${colFirm.id}`}
                                                    data-testid={`matrix-empty-${rowFirm.id}-${colFirm.id}`}
                                                    className="block w-[52px] h-[36px] bg-zinc-950 border border-dashed border-zinc-800 hover:border-amber-500 hover:bg-amber-500/5 flex items-center justify-center transition-colors group"
                                                    title={`Score ${rowFirm.name} → ${colFirm.name}`}
                                                >
                                                    <Sparkles className="w-3 h-3 text-zinc-700 group-hover:text-amber-500 opacity-50 group-hover:opacity-100 transition-opacity" />
                                                </Link>
                                            </td>
                                        );
                                    }
                                    const tone = fitTone(fit);
                                    return (
                                        <td key={colFirm.id} className="p-0.5">
                                            <Link
                                                to={`/app/synergy?key=${encodeURIComponent(`${s.firm_a_id}:${s.firm_b_id}:${s.product_id}`)}`}
                                                data-testid={`matrix-cell-${rowFirm.id}-${colFirm.id}`}
                                                onMouseEnter={() => setHoverCell({ row: rowFirm, col: colFirm, syn: s })}
                                                onMouseLeave={() => setHoverCell(null)}
                                                className={`relative block w-[52px] h-[36px] flex items-center justify-center font-mono font-bold text-sm border transition-all hover:scale-110 hover:z-10 ${hide ? "opacity-15" : ""}`}
                                                style={{
                                                    background: tone.bg,
                                                    borderColor: tone.border,
                                                    color: tone.text,
                                                }}
                                                title={`${rowFirm.name} → ${colFirm.name} · ${fit} fit · ${fmtMoney(s.projected_revenue)}`}
                                            >
                                                {fit}
                                                {s.pinned && <Pin className="absolute top-0.5 right-0.5 w-2 h-2" />}
                                            </Link>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Hover preview */}
            {hoverCell && (
                <div className="mt-4 border border-amber-500/40 bg-zinc-950 p-4 flex items-center justify-between gap-4 flex-wrap animate-fade-in" data-testid="matrix-hover-preview">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400 mb-1">// Selected</div>
                        <div className="font-heading font-bold text-zinc-50 text-base">
                            {hoverCell.row.name} <span className="text-amber-500">×</span> {hoverCell.col.name}
                        </div>
                        <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                            Product: <span className="text-amber-500">{hoverCell.syn.product_name || "—"}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-5">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Fit</div>
                            <div className="font-heading font-black text-2xl text-amber-500">{hoverCell.syn.fit_score}</div>
                        </div>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Projected</div>
                            <div className="font-heading font-black text-2xl text-emerald-400">{fmtMoney(hoverCell.syn.projected_revenue)}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Legend */}
            <div className="mt-6 flex items-center gap-4 flex-wrap font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500" data-testid="matrix-legend">
                <span>Fit:</span>
                <LegendChip color="rgba(16,185,129,0.78)" border="#10B981" label="≥90 strong" />
                <LegendChip color="rgba(59,130,246,0.72)" border="#3B82F6" label="80-89 promising" />
                <LegendChip color="rgba(245,158,11,0.78)" border="#F59E0B" label="70-79 ok" />
                <LegendChip color="rgba(113,113,122,0.55)" border="#52525B" label="<70 weak" />
                <LegendChip color="#0a0a0a" border="#27272A" label="not scored" dashed />
            </div>

            {/* Saturate-row modal — finds partners both in library AND from external suggestions */}
            {saturateFirm && (
                <SaturateRowModal
                    key={saturateFirm.id}
                    firm={saturateFirm}
                    scoredTargetIds={scoredIdsByFirm.get(saturateFirm.id) || EMPTY_SET}
                    onClose={() => setSaturateFirm(null)}
                    onSaved={reload}
                />
            )}
        </div>
    );
}

function Stat({ label, value, accent }) {
    return (
        <div className="bg-black p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{label}</div>
            <div className={`font-heading font-black tracking-tighter text-2xl ${accent ? "text-amber-500" : "text-zinc-100"}`}>{value}</div>
        </div>
    );
}

function LegendChip({ color, border, label, dashed }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span
                className="inline-block w-4 h-4"
                style={{ background: color, border: `1px ${dashed ? "dashed" : "solid"} ${border}` }}
            />
            <span className="text-[10px] text-zinc-400">{label}</span>
        </span>
    );
}
